import CardTypeRef from '@/lib/PaymentMethods/CardTypeRef';
import PayyoTransactionType from '@/lib/PaymentMethods/Payyo/Enums/PayyoTransactionType';

// Standard test cards
// https://docs.stripe.com/terminal/references/testing?terminal-sdk-platform=android#standard-test-cards
export const standardTestCards = [
    {
        card_number: '4242424242424242',
        card_type_ref: CardTypeRef.VISA,
        payment_method: 'visa',
        description: 'Visa',
    },
    {
        card_number: '4000056655665556',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'visa_debit',
        description: 'Visa (debit)',
    },
    {
        card_number: '5555555555554444',
        card_type_ref: CardTypeRef.MASTERCARD,
        payment_method: 'mastercard',
        description: 'Mastercard',
    },
    {
        card_number: '5200828282828210',
        card_type_ref: CardTypeRef.MASTERCARD,
        payment_method: 'mastercard_debit',
        description: 'Mastercard (debit)',
    },
    {
        card_number: '5105105105105100',
        card_type_ref: CardTypeRef.MASTERCARD,
        payment_method: 'mastercard_prepaid',
        description: 'Mastercard (prepaid)',
    },
    {
        card_number: '378282246310005',
        card_type_ref: CardTypeRef.AMEX,
        payment_method: 'amex',
        description: 'American Express',
    },
    {
        card_number: '371449635398431',
        card_type_ref: CardTypeRef.AMEX,
        payment_method: 'amex2',
        description: 'American Express',
    },
    {
        card_number: '6011111111111117',
        card_type_ref: CardTypeRef.DISCOVER,
        payment_method: 'discover',
        description: 'Discover',
    },
    {
        card_number: '6011000990139424',
        card_type_ref: CardTypeRef.DISCOVER,
        payment_method: 'discover2',
        description: 'Discover',
    },
    {
        card_number: '3056930009020004',
        card_type_ref: CardTypeRef.DINERS,
        payment_method: 'diners',
        description: 'Diners Club',
    },
    {
        card_number: '36227206271667',
        card_type_ref: CardTypeRef.DINERS,
        payment_method: 'diners_14digits',
        description: 'Diners Club (14 digit card)',
    },
    {
        card_number: '3566002020360505',
        card_type_ref: CardTypeRef.JCB,
        payment_method: 'jcb',
        description: 'JCB',
    },
];

// Test cards for specific success cases
// https://docs.stripe.com/terminal/references/testing?terminal-sdk-platform=android#test-cards-for-specific-success-cases
export const testCardsForSpecificSuccessCases = [
    {
        card_number: '4001007020000002',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'offline_pin_cvm',
        description: 'Simulates the cardholder being prompted for and entering an offline PIN. The resulting charge has cardholder_verification_method set to offline_pin.',
        hide_for: PayyoTransactionType.CARD_MOTO,
    },
    {
        card_number: '4000008260000075',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'offline_pin_sca_retry',
        description: 'Simulates an SCA-triggered retry flow where a cardholder’s initial contactless charge fails and the reader then prompts the user to insert their card and enter their offline PIN. The resulting charge has cardholder_verification_method set to offline_pin.',
        hide_for: PayyoTransactionType.CARD_MOTO,
    },
    {
        card_number: '4001000360000005',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'online_pin_cvm',
        description: 'Simulates a cardholder being prompted for and entering an online PIN. The resulting charge has cardholder_verification_method set to online_pin.',
        hide_for: PayyoTransactionType.CARD_MOTO,
    },
    {
        card_number: '4000002760000008',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'online_pin_sca_retry',
        description: 'Simulates an SCA-triggered retry flow where a cardholder’s initial contactless charge fails and the reader then prompts the user to input their online PIN. The final charge has cardholder_verification_method set to online_pin.',
        hide_for: PayyoTransactionType.CARD_MOTO,
    },
];

// Test cards for specific error cases
// https://docs.stripe.com/terminal/references/testing?terminal-sdk-platform=android#test-cards-for-specific-error-cases
export const testCardsForSpecificErrorCases = [
    {
        card_number: '4000000000000002',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined',
        description: 'Charge is declined with a card_declined code.',
    },
    {
        card_number: '4000000000009995',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined_insufficient_funds',
        description: 'Charge is declined with a card_declined code. The decline_code attribute is insufficient_funds.',
    },
    {
        card_number: '4000000000009987',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined_lost_card',
        description: 'Charge is declined with a card_declined code. The decline_code attribute is lost_card.',
    },
    {
        card_number: '4000000000009979',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined_stolen_card',
        description: 'Charge is declined with a card_declined code. The decline_code attribute is stolen_card.',
    },
    {
        card_number: '4000000000000069',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined_expired_card',
        description: 'Charge is declined with an expired_card code.',
        // Note weird as it may seem you have to use this card number with a future expiry date to trigger an
        // expired error because the test is based on the number not the actual date.
    },
    {
        card_number: '4000000000000119',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'charge_declined_processing_error',
        description: 'Charge is declined with a processing_error code.',
    },
    {
        card_number: '4000000000005126',
        card_type_ref: CardTypeRef.VISA_DEBIT,
        payment_method: 'refund_fail',
        description: 'Charge succeeds but refunding a captured charge fails asynchronously with a failure_reason of expired_or_canceled_card. Because refund failures are asynchronous, the refund will appear to be successful at first and will only have the failed status on subsequent fetches.',
        hide_for: PayyoTransactionType.CARD_MOTO,
    },
];
